"use client"

import * as React from "react"
import {
  CheckCircle,
  Download,
  Loader2,
  ChevronRight,
  AlertCircle,
  Settings2,
} from "lucide-react"
import { type PlanDisplayData } from "@/lib/plan-display-data"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { type Quote } from "@/lib/api/quotes"
import { useParams, useRouter } from "next/navigation"
import { ROUTES } from "@/lib/routes"
import { usePermissions } from "@/hooks/use-permissions"
import { useQuotesStore } from "@/stores/quotes"
import { useVisitorQuotesStore } from "@/stores/visitor-quotes"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { getProductLabels, getDisplayLabelFromName, getPlanDisplayLabel } from "@/lib/product-labels"
import { toast } from "sonner"

export interface SMECategorySelection {
  categoryLabel: string
  planSlug: string
  planName: string
  yearlyPrice: number
  currency?: string
}

interface QuoteConfirmModalProps {
  plan: PlanDisplayData
  configValues: Record<string, string>
  memberCount?: number
  smeSelections?: SMECategorySelection[]
  onConfirm: () => void
  onClose: () => void
  onSubmitOverride?: () => Promise<Quote>
  rootcurrency?: any
}

export function QuoteConfirmModal({
  plan,
  configValues,
  memberCount = 1,
  smeSelections,
  onConfirm,
  onClose,
  onSubmitOverride,
  rootcurrency,
}: QuoteConfirmModalProps) {
  const isSME = !!smeSelections && smeSelections.length > 0
  const isSMEMulti = !!smeSelections && smeSelections.length > 1
  const [status, setStatus] = React.useState<
    "idle" | "loading" | "done" | "error"
  >("idle")
  const [quote, setQuote] = React.useState<Quote | null>(null)
  const [errorMsg, setErrorMsg] = React.useState("")
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  const router = useRouter()

  // ── Pricing overrides (admin/sales only) ────────────────────────────────
  const { isRole } = usePermissions()
  const canOverridePricing = isRole("super_admin", "admin", "sales")
  const updatePricingOverrides = useQuotesStore((s) => s.updatePricingOverrides)
  const updateVisitorPricingOverrides = useVisitorQuotesStore(
    (s) => s.updateVisitorPricingOverrides
  )
  const fetchGeneratedQuotesFromCookie = useVisitorQuotesStore(
    (s) => s.fetchGeneratedQuotesFromCookie
  )
  const contactedSummary = useVisitorQuotesStore((s) => s.contactedSummary)

  const [novaFee, setNovaFee] = React.useState("")
  const [brokerFee, setBrokerFee] = React.useState("")
  const [discount, setDiscount] = React.useState("")
  const [pricingNote, setPricingNote] = React.useState("")

  // Track whether pricing has been synced to backend
  const [pricingSynced, setPricingSynced] = React.useState(true)
  const [isUpdatingPricing, setIsUpdatingPricing] = React.useState(false)
  const [pricingError, setPricingError] = React.useState("")
  const [pricingWasUpdated, setPricingWasUpdated] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(true)
  const [updatedPricing, setUpdatedPricing] = React.useState<{
    totalWithVat?: number
    totalGross?: number
    totalNet?: number
  } | null>(null)
  // Store individual category quotes with updated pricing (for SME multi-category)
  const [updatedCategoryQuotes, setUpdatedCategoryQuotes] = React.useState<
    Array<{
      id: string
      benefitCategory?: string
      planName: string
      totalWithVat: number
      totalGross: number
      totalNet: number
      memberCount: number
    }>
  >([])

  // Fetch product labels on mount
  React.useEffect(() => {
    getProductLabels()
  }, [])

  // ── Fetch quote details on mount to populate pricing fields ──────────
  const fetchQuoteById = useQuotesStore((s) => s.fetchQuoteById)
  const fetchedQuote = useQuotesStore((s) => s.quote)

  React.useEffect(() => {
    if (isSME) {
      if (!contactedSummary) return
      // Prioritize override values, fall back to pct values
      const novaFeeValue =
        contactedSummary?.novaLoadingOverride ??
        contactedSummary?.novaLoadingPct
      const brokerFeeValue =
        contactedSummary?.brokerLoadingOverride ??
        contactedSummary?.brokerLoadingPct
      if (novaFeeValue != null) {
        setNovaFee(String(novaFeeValue))
      }
      if (brokerFeeValue != null) {
        setBrokerFee(String(brokerFeeValue))
      }
      if (contactedSummary.discountPercentage != null) {
        setDiscount(String(contactedSummary?.discountPercentage))
      }
      setPricingSynced(true)
      return
    }

    if (!plan.slug) return
    fetchQuoteById(plan.slug).then(() => {
      const q = useQuotesStore.getState().quote
      if (!q) return
      // Pre-fill pricing override fields from backend values
      if (q.novaLoadingPct != null) {
        setNovaFee(String(q.novaLoadingPct))
      }
      if (q.brokerLoadingPct != null) {
        setBrokerFee(String(q.brokerLoadingPct))
      }
      if (q.discountPercentage != null) {
        setDiscount(String(q.discountPercentage))
      }
      // These were fetched from the API so they're already saved
      setPricingSynced(true)
    })
  }, [plan.slug, isSME, fetchQuoteById, contactedSummary])

  // Whenever any override field changes, mark as dirty (not synced)
  function handleOverrideChange(
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) {
    setter(value)
    setPricingSynced(false)
    setPricingError("")
  }

  // Has user entered any values at all?
  const hasOverrideValues =
    novaFee !== "" ||
    brokerFee !== "" ||
    discount !== "" ||
    pricingNote.trim() !== ""

  // "Generate Quote" is disabled when there are unsaved pricing overrides
  const generateDisabled =
    canOverridePricing && hasOverrideValues && !pricingSynced

  // ── Update Pricing handler (PATCH to backend) ───────────────────────────
  async function handleUpdatePricing() {
    setIsUpdatingPricing(true)
    setPricingError("")
    try {
      // We need the quote ID — plan.slug IS the quote ID
      const payload: Record<string, unknown> = {}
      if (novaFee !== "" && Number(novaFee) >= 0)
        payload.nova_fee_percentage = Number(novaFee)
      if (brokerFee !== "" && Number(brokerFee) >= 0)
        payload.broker_fee_percentage = Number(brokerFee)
      if (discount !== "" && Number(discount) >= 0)
        payload.discount_percentage = Number(discount)
      if (pricingNote.trim()) payload.note = pricingNote.trim()

      const result = isSME
        ? await updateVisitorPricingOverrides(
            payload as {
              nova_fee_percentage?: number
              broker_fee_percentage?: number
              discount_percentage?: number
              note?: string
            }
          )
        : await updatePricingOverrides(
            plan.slug,
            payload as {
              nova_fee_percentage?: number
              broker_fee_percentage?: number
              discount_percentage?: number
              note?: string
            }
          )

      // Handle SME response with quotes array - sum totals from all categories
      if (isSME && result?.groups && Array.isArray(result.groups)) {
        const quotesArray = result.groups as Array<{
          id: string
          benefitCategory?: string
          totalWithVat: number
          totalGross: number
          totalNet: number
          memberCount: number
          planTier?: { name: string }
        }>
        // Calculate totals from all categories

        // Store individual category quotes for display
        const categoryQuotes = quotesArray.map((q) => ({
          id: q.id,
          benefitCategory: q.benefitCategory,
          planName: q.planTier?.name || plan.name,
          totalWithVat: q.totalWithVat,
          totalGross: q.totalGross,
          totalNet: q.totalNet,
          memberCount: q.memberCount,
        }))
        setUpdatedCategoryQuotes(categoryQuotes)
        setUpdatedPricing({
          totalWithVat: result.total?.totalWithVat,
          totalGross: result.total?.totalGross,
          totalNet: result.total?.totalNet,
        })
        setNovaFee(result.total?.novaLoadingOverride?.toString() || "")
        setBrokerFee(result.total?.brokerLoadingOverride?.toString() || "")
        setDiscount(result.total?.discountPercentage?.toString() || "")
      } else {
        // Regular single quote response
        setUpdatedPricing({
          totalWithVat:
            result?.total?.totalWithVat ?? result?.quote?.totalWithVat,
          totalGross: result?.total?.totalGross ?? result?.quote?.totalGross,
          totalNet: result?.total?.totalNet ?? result?.quote?.totalNet,
        })
      }

      // Update pricing fields from API response (use override values if available)
      const novaOverride = isSME
        ? (result?.total?.novaLoadingOverride ??
          result?.groups?.[0]?.novaLoadingOverride)
        : result?.novaLoadingOverride
      const brokerOverride = isSME
        ? (result?.total?.brokerLoadingOverride ??
          result?.groups?.[0]?.brokerLoadingOverride)
        : result?.brokerLoadingOverride
      const discountPct = isSME
        ? (result?.total?.discountPercentage ??
          result?.groups?.[0]?.discountPercentage)
        : result?.discountPercentage

      if (novaOverride != null) setNovaFee(String(novaOverride))
      if (brokerOverride != null) setBrokerFee(String(brokerOverride))
      if (discountPct != null) setDiscount(String(discountPct))

      setPricingSynced(true)
      toast.success("Pricing updated successfully")
      
      // Mark that pricing was updated for individual quotes
      // We'll refresh the quotes when the modal closes to avoid unmounting the modal
      if (!isSME) {
        setPricingWasUpdated(true)
      }
    } catch (err: unknown) {
      console.error("err", err)
      const apiError = err as { error?: { message?: string } }
      setPricingError(
        apiError?.error?.message ??
          "Failed to update pricing. Please try again."
      )
    } finally {
      setIsUpdatingPricing(false)
    }
  }

  // ── Custom close handler ──────────────
  async function handleClose() {
    // If pricing was updated for individual quotes, refresh the quotes list before closing
    if (!isSME && pricingWasUpdated) {
      try {
        await fetchGeneratedQuotesFromCookie()
      } catch (err) {
        console.error("Failed to refresh quotes:", err)
      }
    }
    
    setIsOpen(false)
    onClose()
  }

  // ── Generate Quote handler ──────────────────────────────────────────────
  async function handleConfirm() {
    setStatus("loading")
    setErrorMsg("")
    try {
      if (!onSubmitOverride) {
        throw new Error("No submit handler provided.")
      }
      const data = await onSubmitOverride()
      setQuote(data)
      setStatus("done")
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      setErrorMsg(
        apiError?.error?.message ??
          (err instanceof Error
            ? err.message
            : "Failed to generate quote. Please try again.")
      )
      setStatus("error")
    }
  }

  const displayFields = Object.entries(plan.benefits || {})
    .slice(0, 8)
    .map(([key, value]) => ({
      id: key,
      label: key
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
      defaultValue: value,
    }))

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose()
      }
    }}>
      <DialogContent className="max-h-[90vh] w-full max-w-xl overflow-y-auto border-0 bg-white p-0 shadow-2xl sm:max-w-2xl sm:rounded-2xl dark:bg-slate-900 [&>button]:top-4 [&>button]:right-4">
        <DialogTitle className="sr-only">Quote Confirmation</DialogTitle>
        {/* Loading state */}
        {status === "loading" && (
          <div className="flex flex-col items-center justify-center gap-4 px-6 py-16">
            <Loader2 className="h-12 w-12 animate-spin text-nova-600" />
            <p className="text-base font-semibold text-slate-700 dark:text-slate-300">
              Generating your quote…
            </p>
          </div>
        )}

        {/* Error state */}
        {status === "error" && (
          <div className="flex flex-col items-center gap-4 px-6 py-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="h-9 w-9 text-red-600 dark:text-red-400" />
            </div>
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Something went wrong
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {errorMsg}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setStatus("idle")}
                className="rounded-full bg-nova-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-nova-700"
              >
                Try Again
              </button>
              <button
                onClick={onClose}
                className="rounded-full border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Done state */}
        {status === "done" && (
          <div className="flex flex-col items-center gap-4 px-6 py-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <CheckCircle className="h-9 w-9 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Quote Generated!
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {isSME ? (
                  isSMEMulti ? (
                    `Your combined quote for ${smeSelections!.length} categories is ready.`
                  ) : (
                    `Your SME quote for 1 category is ready.`
                  )
                ) : (
                  <>
                    Your quote for{" "}
                    <span className="font-semibold text-slate-700 dark:text-slate-200">
                      {plan.name}
                    </span>{" "}
                    is ready.
                  </>
                )}
              </p>
            </div>
            <div className="w-full rounded-xl bg-slate-50 px-5 py-4 dark:bg-slate-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Total Premium</span>
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {isSME
                    ? (updatedPricing?.totalWithVat != null
                        ? updatedPricing?.totalWithVat
                        : smeSelections!.reduce((s, c) => s + c.yearlyPrice, 0)
                      ).toLocaleString()
                    : (
                        quote?.totalWithVat ?? quote?.premium_total
                      )?.toLocaleString()}{" "}
                  AED{" "}
                  <span className="text-xs font-normal text-slate-400">
                    /yr
                  </span>
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {isSME ? "Categories" : "Members covered"}
                </span>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {isSME ? smeSelections!.length : memberCount}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  {quote?.status ?? "generated"}
                </span>
              </div>
            </div>
            <div className="flex w-full gap-3">
              <button
                onClick={() => {
                  onConfirm()
                  if (quote?.id) {
                    router.push(ROUTES.quoteDetail(locale, quote.id))
                  }
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-nova-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-nova-700"
              >
                View Quote Details
                <ChevronRight className="h-4 w-4" />
              </button>
              {(() => {
                const pdfDoc = quote?.documents?.find(
                  (d) => d.type === "policy_doc" && d.file_url
                )
                return pdfDoc ? (
                  <a
                    href={pdfDoc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </a>
                ) : null
              })()}
            </div>
          </div>
        )}

        {/* Idle state */}
        {status === "idle" && (
          <>
            {/* Header */}
            <div className="border-b border-slate-100 px-6 pt-6 pb-4 dark:border-slate-700">
              <p className="text-sm font-semibold text-nova-600 dark:text-nova-400">
                Quote Summary
              </p>
              <h2 className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
                {isSME
                  ? isSMEMulti
                    ? `SME — ${smeSelections!.length} Categories`
                    : `SME — 1 Category`
                  : plan.name}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {isSME
                  ? smeSelections!.map((s) => s.categoryLabel).join(" · ")
                  : getPlanDisplayLabel({
                      coverageArea: plan.coverageArea,
                      category: plan.category,
                      memberCount,
                      productCode: plan.productCode,
                      productName: plan.productName,
                    })}
              </p>
            </div>

            {/* Price summary */}
            <div className="bg-slate-800 px-6 py-4">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-slate-400">Total Premium</p>
                  <p className="text-2xl font-bold text-white">
                    {isSME
                      ? smeSelections!
                          .reduce((s, c) => s + c.yearlyPrice, 0)
                          .toLocaleString()
                      : (
                          fetchedQuote?.totalWithVat ?? quote?.premium_total
                        )?.toLocaleString()}{" "}
                    <span className="text-sm font-normal text-slate-300">
                      {rootcurrency?.currency ?? fetchedQuote?.currency}
                    </span>
                  </p>
                </div>
                {!isSME && (
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Coverage Area</p>
                    <p className="text-sm font-semibold text-white">
                      {plan.coverageArea}
                    </p>
                    <p className="text-xs text-slate-400">
                      TPA:{" "}
                      {typeof fetchedQuote?.ratesSnapshot?.tpa_selected === "string"
                        ? fetchedQuote.ratesSnapshot.tpa_selected
                        : plan.tpa}
                    </p>
                    {(plan.productCode?.startsWith("micro_sme") || plan.productCode?.startsWith("sme")) && (
                      <p className="text-xs text-slate-400">
                        OP Network:{" "}
                        {plan.selectedOpNetwork ?? plan.planTier?.networkOp}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* SME multi-category breakdown OR individual benefits */}
            <div className="px-6 py-4">
              {isSME ? (
                <>
                  <p className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
                    Category Breakdown
                  </p>
                  <div className="divide-y divide-slate-100 dark:divide-slate-700">
                    {smeSelections!.map((sel) => {
                      // Extract category letter from "Category A" -> "A"
                      const categoryLetter = sel.categoryLabel.split(" ").pop()
                      // Find updated quote for this category if available
                      const updatedQuote = updatedCategoryQuotes.find(
                        (q) => q.benefitCategory === categoryLetter
                      )
                      const displayPrice =
                        updatedQuote?.totalWithVat ?? sel.yearlyPrice
                      const hasUpdatedPrice = updatedQuote != null

                      return (
                        <div
                          key={sel.categoryLabel}
                          className="flex items-center justify-between py-2.5"
                        >
                          <div>
                            <span className="mr-2 rounded-full bg-nova-100 px-2 py-0.5 text-[10px] font-bold text-white dark:bg-nova-900/30 dark:text-nova-400">
                              {sel.categoryLabel}
                            </span>
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                              {sel.planName}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-slate-900 dark:text-white">
                            {displayPrice.toLocaleString()}{" "}
                            <span className="text-[10px] font-normal text-slate-400">
                              {sel.currency}/yr
                            </span>
                            {hasUpdatedPrice && (
                              <span className="ml-1 text-[10px] text-emerald-600">
                                (updated)
                              </span>
                            )}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase dark:text-slate-500">
                    Selected Benefits
                  </p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {displayFields.map((field) => (
                      <div key={field.id} className="text-xs">
                        <span className="text-slate-500 dark:text-slate-400">
                          {field.label}:{" "}
                        </span>
                        <span className="font-bold text-slate-900 dark:text-white">
                          {configValues[field.id] ?? field.defaultValue}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Total quote price */}
            <div className="mx-6 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 dark:bg-slate-800">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                Total Quote Price
              </span>
              <div className="text-right">
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {updatedPricing?.totalWithVat != null
                    ? updatedPricing?.totalWithVat.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : isSME
                      ? smeSelections!
                          .reduce((s, c) => s + c.yearlyPrice, 0)
                          .toLocaleString()
                      : ((
                          fetchedQuote?.totalWithVat ??
                          plan.yearlyPrice ??
                          plan.monthlyPrice
                        )?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }) ?? "—")}{" "}
                  <span className="text-xs font-normal text-slate-400">
                    {rootcurrency?.currency ??
                      fetchedQuote?.currency ??
                      plan.priceBreakdown?.currency}
                  </span>
                </span>
                {updatedPricing?.totalWithVat != null && (
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                    Updated with pricing adjustments
                  </p>
                )}
              </div>
            </div>

            {/* ── Pricing Adjustments (admin/sales only) — always visible ── */}
            {canOverridePricing && (
              <div className="border-t border-slate-100 px-6 py-4 dark:border-slate-700">
                <div className="mb-4 flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-nova-600 dark:text-nova-400" />
                  <span className="text-xs font-semibold tracking-wide text-slate-600 uppercase dark:text-slate-300">
                    Pricing Adjustments
                  </span>
                  {pricingSynced && hasOverrideValues && (
                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      Saved
                    </span>
                  )}
                  {!pricingSynced && hasOverrideValues && (
                    <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      Unsaved
                    </span>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Nova Fee */}
                  <div>
                    <label
                      htmlFor="nova-fee"
                      className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      Nova Fee (%)
                    </label>
                    <Input
                      id="nova-fee"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0"
                      value={novaFee}
                      onChange={(e) =>
                        handleOverrideChange(setNovaFee, e.target.value)
                      }
                      className="h-11 [appearance:textfield] rounded-xl border-slate-200 bg-white px-4 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:border-slate-800 dark:bg-slate-900 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>

                  {/* Broker Fee */}
                  <div>
                    <label
                      htmlFor="broker-fee"
                      className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      Broker Fee (%)
                    </label>
                    <Input
                      id="broker-fee"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0"
                      value={brokerFee}
                      onChange={(e) =>
                        handleOverrideChange(setBrokerFee, e.target.value)
                      }
                      className="h-11 [appearance:textfield] rounded-xl border-slate-200 bg-white px-4 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:border-slate-800 dark:bg-slate-900 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>

                  {/* Discount / Additional Charges */}
                  {/*
                  <div>
                    <label
                      htmlFor="discount"
                      className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      Discount (%)
                    </label>
                    <input
                      id="discount"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={discount}
                      onChange={(e) =>
                        handleOverrideChange(setDiscount, e.target.value)
                      }
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-nova-500 focus:ring-1 focus:ring-nova-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                  */}

                  {/* Note */}
                  <div>
                    <label
                      htmlFor="pricing-note"
                      className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      Note
                    </label>
                    <textarea
                      id="pricing-note"
                      rows={2}
                      placeholder="Reason for pricing changes…"
                      value={pricingNote}
                      onChange={(e) =>
                        handleOverrideChange(setPricingNote, e.target.value)
                      }
                      className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-nova-500 focus:ring-1 focus:ring-nova-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                </div>

                {/* Pricing error */}
                {pricingError && (
                  <p className="mt-2 text-xs text-red-500">{pricingError}</p>
                )}

                {/* Update Pricing button */}
                <Button
                  type="button"
                  onClick={handleUpdatePricing}
                  disabled={
                    pricingSynced || isUpdatingPricing || !hasOverrideValues
                  }
                  className="mt-4 w-full"
                >
                  {isUpdatingPricing && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {pricingSynced && hasOverrideValues
                    ? "Pricing Updated ✓"
                    : "Update Pricing"}
                </Button>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-700">
              <button
                onClick={handleConfirm}
                disabled={generateDisabled}
                className={cn(
                  "flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors",
                  generateDisabled
                    ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                    : "bg-nova-600 text-white hover:bg-nova-700"
                )}
              >
                Generate Quote
              </button>
              <button
                onClick={handleClose}
                // disabled={generateDisabled}
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
